import React from 'react';
import { API_BASE_URL } from '../services/api';
import { ThinkingStep } from '../types';
import { useWebSocketWithStage } from '../context/websocket';

export function useThinkingStream(params: {
  userId?: string | null;
  token?: string | null;
  chatId?: string | null;         // regular chat
  jobId?: string | null;          // file job => chatId becomes "__file__:<jobId>"
}, isLoading: boolean, setIsLoading: (isLoading: boolean) => void, 
 updateThinkingSteps: (value: React.SetStateAction<ThinkingStep[]>) => void) {
  const { userId, token, chatId, jobId } = params;
  const resolvedChatId = jobId ? `__file__:${jobId}` : chatId || undefined;

  const wsUrl =
    userId && resolvedChatId
      ? `${API_BASE_URL.replace(/^https/, 'wss').replace(/^http/, 'ws')}/ws/thinking/${userId}/${resolvedChatId}`
      : undefined;

  const { wsRef, readyState, stageTrigger } = useWebSocketWithStage(
    wsUrl,
    token ? token : undefined,
    undefined
  );

  //const [steps, setSteps] = React.useState<ThinkingStep[]>([]);

  React.useEffect(() => {
    updateThinkingSteps([]); // reset when source changes
  }, [wsUrl]);

  React.useEffect(() => {
    const socket = wsRef.current;
    if (!socket || stageTrigger < WebSocket.OPEN || !isLoading) return;

    setIsLoading(stageTrigger === WebSocket.CONNECTING || stageTrigger === WebSocket.OPEN)

    const onMessage = (event: MessageEvent) => {
      try {
        const data = JSON.parse(event.data);
        // normalize timestamp to number for UI sorting if needed

        const t = typeof data.timestamp === 'string' ? Date.parse(data.timestamp) : data.timestamp;
        updateThinkingSteps(prev => [...prev, { ...data, timestamp: t ?? Date.now() }]);
      } catch (e) {
        console.error('Failed to parse thinking step', e, event.data);
      }
    };

    socket.onmessage = onMessage;
    return () => {
      if (socket) socket.onmessage = null;
    };
  }, [stageTrigger, readyState, wsRef, setIsLoading]);

  const reset = React.useCallback(() => updateThinkingSteps([]), []);

  return { readyState, reset, wsRef};
}
